import { apiRequest } from './api';
import { ApiResponse, Despesa, DespesasFormData, DespesasFilters, DespesasStats } from '../types';

class DespesasService {
  // Listar despesas com filtros e paginação
  async getDespesas(filters: DespesasFilters & { page?: number; limit?: number } = {}): Promise<ApiResponse<Despesa[]> & { stats?: DespesasStats }> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiRequest.get<Despesa[]>(`/despesas?${params.toString()}`);
    return response as ApiResponse<Despesa[]> & { stats?: DespesasStats };
  }

  // Obter uma despesa específica
  async getDespesa(id: number): Promise<ApiResponse<Despesa>> {
    return await apiRequest.get<Despesa>(`/despesas/${id}`);
  }

  // Criar nova despesa
  async cadastrarDespesa(data: DespesasFormData): Promise<ApiResponse<Despesa>> {
    return await apiRequest.post<Despesa>('/despesas', data);
  }

  // Atualizar despesa
  async atualizarDespesa(id: number, data: Partial<DespesasFormData>): Promise<ApiResponse<Despesa>> {
    return await apiRequest.put<Despesa>(`/despesas/${id}`, data);
  }

  // Marcar despesa como paga com dados completos de pagamento
  async pagarDespesa(id: number, paymentData: {
    data_pagamento: string;
    valor_pago: number;
    forma_pgto: string;
    caixa_financeiro_id: number | null;
    observacoes?: string;
  }): Promise<ApiResponse<Despesa>> {
    return await apiRequest.post<Despesa>(`/despesas/${id}/pagar`, paymentData);
  }

  // Cancelar pagamento
  async cancelarPagamento(id: number): Promise<ApiResponse<Despesa>> {
    return await apiRequest.post<Despesa>(`/despesas/${id}/cancelar-pagamento`);
  }

  // Cancelar despesa
  async cancelarDespesa(id: number, motivoCancel: string): Promise<ApiResponse<Despesa>> {
    return await apiRequest.post<Despesa>(`/despesas/${id}/cancelar`, {
      motivo_cancel: motivoCancel
    });
  }

  // Deletar despesa
  async deletarDespesa(id: number): Promise<ApiResponse<void>> {
    return await apiRequest.delete(`/despesas/${id}`);
  }

  // Obter estatísticas
  async getStats(filters: { mes?: number; ano?: number } = {}): Promise<ApiResponse<DespesasStats>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    return await apiRequest.get<DespesasStats>(`/despesas/stats?${params.toString()}`);
  }

  // Métodos auxiliares para formatação
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('pt-BR');
  }

  // Manter métodos de compatibilidade para categorias antigas
  getCategoriaLabel(categoria: string): string {
    const labels: Record<string, string> = {
      'aluguel': 'Aluguel',
      'salarios': 'Salários', 
      'impostos': 'Impostos',
      'manutencao_veiculos': 'Manutenção de Veículos',
      'outros': 'Outros'
    };
    return labels[categoria] || categoria;
  }

  getTipoLabel(tipo: string): string {
    const labels: Record<string, string> = {
      'fixa': 'Fixa',
      'variavel': 'Variável'
    };
    return labels[tipo] || tipo;
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pendente': 'Pendente',
      'pago': 'Pago',
      'vencido': 'Vencido'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
    const colors: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
      'pendente': 'warning',
      'pago': 'success',
      'vencido': 'error'
    };
    return colors[status] || 'default';
  }

  isOverdue(dataVencimento: string, status: string): boolean {
    if (status === 'pago') return false;
    const today = new Date();
    const dueDate = new Date(dataVencimento);
    return dueDate < today;
  }
}

const despesasServiceInstance = new DespesasService();

const despesasService = {
  getDespesas: despesasServiceInstance.getDespesas.bind(despesasServiceInstance),
  getDespesa: despesasServiceInstance.getDespesa.bind(despesasServiceInstance),
  cadastrarDespesa: despesasServiceInstance.cadastrarDespesa.bind(despesasServiceInstance),
  atualizarDespesa: despesasServiceInstance.atualizarDespesa.bind(despesasServiceInstance),
  cancelarDespesa: despesasServiceInstance.cancelarDespesa.bind(despesasServiceInstance),
  deletarDespesa: despesasServiceInstance.deletarDespesa.bind(despesasServiceInstance),
  pagarDespesa: despesasServiceInstance.pagarDespesa.bind(despesasServiceInstance),
  cancelarPagamento: despesasServiceInstance.cancelarPagamento.bind(despesasServiceInstance),

  getStats: despesasServiceInstance.getStats.bind(despesasServiceInstance),
  formatCurrency: despesasServiceInstance.formatCurrency.bind(despesasServiceInstance),
  formatDate: despesasServiceInstance.formatDate.bind(despesasServiceInstance),
  getCategoriaLabel: despesasServiceInstance.getCategoriaLabel.bind(despesasServiceInstance),
  getTipoLabel: despesasServiceInstance.getTipoLabel.bind(despesasServiceInstance),
  getStatusLabel: despesasServiceInstance.getStatusLabel.bind(despesasServiceInstance),
  getStatusColor: despesasServiceInstance.getStatusColor.bind(despesasServiceInstance),
  isOverdue: despesasServiceInstance.isOverdue.bind(despesasServiceInstance)
};

export default despesasService; 